/**
 * The counters shape derivation folds over.
 *
 * Deriving a shape by reading every object is O(objects), and it runs on every
 * lens open. These counters make it O(keys): a commit pays the cost of its own
 * delta, and derivation never touches an object again.
 *
 * The cost is the thing 0005 warned about — counters can drift from the truth
 * and nothing notices. So `rebuildDigest` rescans, and the parity test between
 * the two is not optional decoration; it is the only reason to trust these.
 */

import { DERIVATIVE_OF, type Role } from '@morph/spec';
import type { ObjectId, PropertyKeyId } from './ids.js';
import type { Scope } from './scope.js';

/** One object's contribution to a scope's counters, computed once and applied
 *  with a sign — so removing an object is the same code path as adding one, and
 *  the two cannot disagree. */
export interface Contribution {
  readonly populated: readonly PropertyKeyId[];
  readonly roles: readonly Role[];
  readonly enums: readonly (readonly [PropertyKeyId, string])[];
  /** The object's number, score and date values, for the bounded ordered
   *  sketch. Carried on the same `Contribution` the marginal sketches use, so a
   *  removal is the same code path as an addition. */
  readonly ordered: readonly (readonly [PropertyKeyId, string])[];
  readonly relations: number;
  /** Of those relations, how many point OUT of this lens's own document — into
   *  a manifest the scope declared in `across`. Zero for every scope that
   *  declared none, which is every scope but the ones that asked. */
  readonly crossDoc: number;
}

/**
 * Which derivative roles THIS object owes, given the sources it carries.
 *
 * One helper for both directions, for the reason `Contribution` itself gives
 * about the sketches: *"a removal is the same code path as an addition."* Two
 * copies of this predicate could disagree, and a digest that decrements
 * differently from how it incremented drifts silently — which is exactly what
 * `rebuildDigest` exists to repair and what nobody would think to look for.
 */
function missingDerivatives(roles: readonly Role[]): Array<[Role, number]> {
  const held = new Set(roles);
  const out: Array<[Role, number]> = [];
  for (const [derivative, source] of Object.entries(DERIVATIVE_OF)) {
    if (held.has(source as Role) && !held.has(derivative as Role)) {
      out.push([derivative as Role, 1]);
    }
  }
  return out;
}

interface EnumSketch { counts: Map<string, number>; saturated: boolean }

export class Counters {
  readonly scope: Scope;
  readonly key: string;
  revision = 0;
  total = 0;
  readonly members = new Set<ObjectId>();
  readonly populatedByKey = new Map<PropertyKeyId, number>();
  readonly objectsByRole = new Map<Role, number>();
  readonly enumValues = new Map<PropertyKeyId, EnumSketch>();
  /** Private bounded sketches for number, score and date keys — the evidence a
   *  threshold control needs, which coverage cannot supply. */
  readonly orderedValues = new Map<PropertyKeyId, EnumSketch>();
  /**
   * Distinct value PAIRS, per unordered pair of enum KEYS.
   *
   * `enumValues` is marginal: it says how many values a role holds and nothing
   * about how two roles cross. A second grouping turns entirely on the cross —
   * two roles can each hold four values and never divide each other, and the
   * board then draws a single sub-value under every column and no heading at
   * all, while the spec that asked for it still differs in the pooled genome.
   * @Codex found the generator emitting on marginal evidence alone.
   *
   * Same increment/decrement discipline as `enumValues`, from the SAME
   * `Contribution`, so a removal is the same code path as an addition and
   * `rebuildDigest`'s parity check covers both.
   */
  readonly enumPairs = new Map<string, EnumSketch>();
  /** How many REFERENCES the members hold, summed. An edge-volume signal, and
   *  deliberately not a density: an object with five references contributes
   *  five. Kept because "how connected is this pool" is a real question; it is
   *  simply not the question a surfacing decision asks. */
  relationTotal = 0;
  /** Objects carrying at least one reference, over which `relationDensity` is
   *  computed. The comment on `crossDocTotal` below has always carried the
   *  argument for this counter existing — *"objects rather than references …
   *  a card with three references into one note is still one card with a
   *  relation to draw"* — written about that field and applied only there,
   *  while `relationDensity` divided `relationTotal` and let one highly
   *  connected outlier decide whether a whole pool surfaced its relations.
   *  Found by @Codex. */
  relationObjects = 0;
  /** Objects carrying at least one reference into a DECLARED other document.
   *  Objects rather than references: what a layout decision turns on is
   *  "does a card here have somewhere else to point", and a card with three
   *  references into one note is still one card with a relation to draw. */
  crossDocTotal = 0;

  /**
   * Objects carrying a derivative's SOURCE but not the derivative itself.
   *
   * Keyed by the derivative role, and a containment is a count of zero. Task
   * `0131`: *"two coverages of 0.5 are equally consistent with total overlap
   * and with none"*, so a card deciding whether to draw the cheap picture
   * instead of the source needs a joint fact and per-role counts cannot supply
   * one.
   *
   * A counter rather than a set, maintained the way `relationObjects` is, so it
   * costs two set lookups per object per commit and nothing per read. There are
   * two derivative roles in the vocabulary; this is not an index.
   */
  readonly sourceWithoutDerivative = new Map<Role, number>();

  /** Set once a sketch stops accepting new distinct values. A saturated sketch
   *  cannot be decremented back to the truth, which is exactly the drift
   *  `rebuildDigest` exists to repair — recorded rather than hidden. */
  saturated = false;

  constructor(scope: Scope, key: string, readonly enumCap: number) {
    this.scope = scope;
    this.key = key;
  }

  has(id: ObjectId): boolean { return this.members.has(id); }

  add(id: ObjectId, c: Contribution): void {
    if (this.members.has(id)) return;
    this.members.add(id);
    this.total += 1;
    this.relationTotal += c.relations;
    if (c.relations > 0) this.relationObjects += 1;
    if (c.crossDoc > 0) this.crossDocTotal += 1;
    for (const k of c.populated) bump(this.populatedByKey, k, 1);
    for (const r of c.roles) bump(this.objectsByRole, r, 1);
    for (const [d, n] of missingDerivatives(c.roles)) bump(this.sourceWithoutDerivative, d, n);
    for (const [k, v] of c.enums) this.addValue(k, v);
    for (const [k, v] of c.ordered) this.addOrderedValue(k, v);
    for (const [k, v] of pairsOf(c.enums)) this.addPair(k, v);
  }

  remove(id: ObjectId, c: Contribution): void {
    if (!this.members.has(id)) return;
    this.members.delete(id);
    this.total -= 1;
    this.relationTotal -= c.relations;
    if (c.relations > 0) this.relationObjects -= 1;
    if (c.crossDoc > 0) this.crossDocTotal -= 1;
    for (const k of c.populated) bump(this.populatedByKey, k, -1);
    for (const r of c.roles) bump(this.objectsByRole, r, -1);
    for (const [d, n] of missingDerivatives(c.roles)) bump(this.sourceWithoutDerivative, d, -n);
    for (const [k, v] of c.enums) removeValue(this.sketch(k), v);
    for (const [k, v] of c.ordered) removeValue(this.orderedSketch(k), v);
    for (const [k, v] of pairsOf(c.enums)) removeValue(this.pairSketch(k), v);
  }

  distinct(key: PropertyKeyId): number { return this.enumValues.get(key)?.counts.size ?? 0; }

  private sketch(k: PropertyKeyId): EnumSketch {
    let s = this.enumValues.get(k);
    if (!s) { s = { counts: new Map(), saturated: false }; this.enumValues.set(k, s); }
    return s;
  }

  private orderedSketch(k: PropertyKeyId): EnumSketch {
    let s = this.orderedValues.get(k);
    if (!s) { s = { counts: new Map(), saturated: false }; this.orderedValues.set(k, s); }
    return s;
  }

  private addOrderedValue(k: PropertyKeyId, v: string): void {
    const s = this.orderedSketch(k);
    const seen = s.counts.get(v);
    if (seen === undefined && s.counts.size >= this.enumCap) {
      s.saturated = true;
      this.saturated = true;
      return;
    }
    s.counts.set(v, (seen ?? 0) + 1);
  }

  private pairSketch(k: string): EnumSketch {
    let s = this.enumPairs.get(k);
    if (!s) { s = { counts: new Map(), saturated: false }; this.enumPairs.set(k, s); }
    return s;
  }

  /** The cap applies to pairs too. Past it the joint distribution is as
   *  free-form as a saturated marginal one, and the same argument holds: a
   *  decision that needs more of a user's values than this is not a decision
   *  this layer should be making. */
  private addPair(k: string, v: string): void {
    const s = this.pairSketch(k);
    const seen = s.counts.get(v);
    if (seen === undefined && s.counts.size >= this.enumCap) {
      s.saturated = true;
      this.saturated = true;
      return;
    }
    s.counts.set(v, (seen ?? 0) + 1);
  }

  private addValue(k: PropertyKeyId, v: string): void {
    const s = this.sketch(k);
    const seen = s.counts.get(v);
    if (seen === undefined && s.counts.size >= this.enumCap) {
      // Past the cap the role is free-form, not an enumeration. Stop growing —
      // holding more of a user's values than the decision needs is the one
      // thing this whole layer exists to avoid.
      s.saturated = true;
      this.saturated = true;
      return;
    }
    s.counts.set(v, (seen ?? 0) + 1);
  }
}

/**
 * One object's enum values, crossed — every unordered pair of DISTINCT keys.
 *
 * Sorted by key first so `(a, b)` and `(b, a)` produce one entry rather than
 * two that can fall out of step. Two keys carrying the same role are skipped:
 * a role crossed with itself is not a second grouping.
 */
function pairsOf(
  enums: readonly (readonly [PropertyKeyId, string])[],
): [string, string][] {
  if (enums.length < 2) return [];
  const sorted = [...enums].sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0));
  const out: [string, string][] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    for (let j = i + 1; j < sorted.length; j += 1) {
      const [ka, va] = sorted[i]!;
      const [kb, vb] = sorted[j]!;
      if (ka === kb) continue;
      out.push([`${ka}|${kb}`, `${va}\u0000${vb}`]);
    }
  }
  return out;
}

function removeValue(s: EnumSketch, v: string): void {
  const seen = s.counts.get(v);
  if (seen === undefined) return;
  if (seen <= 1) s.counts.delete(v);
  else s.counts.set(v, seen - 1);
}

function bump<K>(m: Map<K, number>, k: K, by: number): void {
  const next = (m.get(k) ?? 0) + by;
  if (next <= 0) m.delete(k);
  else m.set(k, next);
}
